import type { TranslationKeys } from "../../../i18n/types";

/*
 * DAT-906 · The columns of the three rule tables on Team → Wage Settings, in the order the tables show
 * them — one definition, read by the editable tables and by the wage breakdown's read-only preview of the
 * same rows, so the two can never disagree on a header or its order.
 */

export type WageRuleColumn<TId extends string> = {
	id: TId;
	headerKey: TranslationKeys;
	/** The editable table's floor for the column (px). */
	minWidth: number;
	/** Pinned at `minWidth` rather than floored (the Rate type switch, which must not stretch). */
	fixed?: true;
	/** Drawn only when the department has jobs, as the tables do. */
	jobsOnly?: true;
};

export const HOURS_RATE_COLUMNS = [
	{ id: "name", headerKey: "department.config.rates.hoursRate.col.shiftName", minWidth: 150 },
	{ id: "hours", headerKey: "department.config.rates.hoursRate.col.hours", minWidth: 200 },
	{ id: "role", headerKey: "department.config.rates.hoursRate.col.role", minWidth: 120 },
	{ id: "job", headerKey: "department.config.rates.hoursRate.col.job", minWidth: 140, jobsOnly: true },
	{ id: "rate", headerKey: "department.config.rates.hoursRate.col.rate", minWidth: 120 },
	{ id: "rateType", headerKey: "department.config.rates.hoursRate.col.rateType", minWidth: 180, fixed: true },
	{ id: "charge", headerKey: "department.config.rates.hoursRate.col.charge", minWidth: 100 },
	{ id: "trainingStatus", headerKey: "department.config.rates.hoursRate.col.trainingStatus", minWidth: 150 },
	{ id: "employmentType", headerKey: "department.config.rates.hoursRate.col.employmentType", minWidth: 150 },
	{ id: "weekend", headerKey: "department.config.rates.hoursRate.col.weekend", minWidth: 100 },
	{ id: "union", headerKey: "department.config.rates.hoursRate.col.union", minWidth: 120 },
	{ id: "agency", headerKey: "department.config.rates.hoursRate.col.agency", minWidth: 150 },
] as const satisfies readonly WageRuleColumn<string>[];

export const OVERTIME_COLUMNS = [
	{ id: "name", headerKey: "department.config.rates.overtime.col.name", minWidth: 140 },
	{ id: "hoursLimit", headerKey: "department.config.rates.overtime.col.hoursLimit", minWidth: 100 },
	{ id: "role", headerKey: "department.config.rates.overtime.col.role", minWidth: 140 },
	{ id: "job", headerKey: "department.config.rates.overtime.col.job", minWidth: 140, jobsOnly: true },
	{ id: "employmentType", headerKey: "department.config.rates.overtime.col.employmentType", minWidth: 130 },
	{ id: "union", headerKey: "department.config.rates.overtime.col.union", minWidth: 120 },
	{ id: "type", headerKey: "department.config.rates.overtime.col.type", minWidth: 100 },
	{ id: "daysFrom", headerKey: "department.config.rates.overtime.col.daysFrom", minWidth: 90 },
	{ id: "daysTo", headerKey: "department.config.rates.overtime.col.daysTo", minWidth: 90 },
	{ id: "rate", headerKey: "department.config.rates.overtime.col.rate", minWidth: 120 },
	{ id: "rateType", headerKey: "department.config.rates.overtime.col.rateType", minWidth: 100 },
] as const satisfies readonly WageRuleColumn<string>[];

export const BREAK_RULES_COLUMNS = [
	{ id: "name", headerKey: "department.config.rates.breakRules.col.name", minWidth: 140 },
	{ id: "breakLimit", headerKey: "department.config.rates.breakRules.col.breakLimit", minWidth: 120 },
	{ id: "breakStart", headerKey: "department.config.rates.breakRules.col.breakStart", minWidth: 120 },
	{ id: "role", headerKey: "department.config.rates.breakRules.col.role", minWidth: 140 },
	{ id: "job", headerKey: "department.config.rates.breakRules.col.job", minWidth: 140, jobsOnly: true },
	{ id: "employmentType", headerKey: "department.config.rates.breakRules.col.employmentType", minWidth: 130 },
	{ id: "union", headerKey: "department.config.rates.breakRules.col.union", minWidth: 120 },
	{ id: "type", headerKey: "department.config.rates.breakRules.col.type", minWidth: 100 },
	{ id: "breakTime", headerKey: "department.config.rates.breakRules.col.breakTime", minWidth: 120 },
] as const satisfies readonly WageRuleColumn<string>[];

export type HoursRateColumnId = (typeof HOURS_RATE_COLUMNS)[number]["id"];
export type OvertimeColumnId = (typeof OVERTIME_COLUMNS)[number]["id"];
export type BreakRulesColumnId = (typeof BREAK_RULES_COLUMNS)[number]["id"];

/** Every editable table ends with its row actions (delete); the read-only preview has none. */
export const actionsColumn = (headerKey: TranslationKeys): WageRuleColumn<"actions"> => ({ id: "actions", headerKey, minWidth: 80 });

/** The columns a department's table shows: the Job column only where it has jobs. */
export const visibleColumns = <TColumn extends WageRuleColumn<string>>(columns: readonly TColumn[], showJobColumn: boolean): TColumn[] =>
	columns.filter((column) => showJobColumn || column.jobsOnly !== true);

/** A header cell's width rule in the editable table. */
export const columnWidthSx = (column: WageRuleColumn<string>) =>
	column.fixed ? { width: column.minWidth, minWidth: column.minWidth, maxWidth: column.minWidth } : { minWidth: `${column.minWidth}px` };
